import React, {useEffect, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {flushSync} from 'react-dom';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {v4 as uuidv4} from 'uuid';

import {createAddress, getAddressesSuggestList, getAddressInfoFromString} from 'api/geo';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {AsyncSelect} from 'components/ui-kit/Select';
import {COUNTRY_NAMING} from 'core/constants/env';
import {profileCountriesState$} from 'models/profile/common';
import {
    addFactories,
    factoryOperationsState$,
    isCreateModalOpenChanged,
} from 'models/profile/factories/factoryOperations';
import {Address} from 'types/geo';
import {CreateFactory, CreateFactoryLine, ProfileCountries} from 'types/profile';
import {formatAddressInfoToRequest} from 'utils/address';

import {factoriesSchema} from '../helpers';

import {FactoryLines} from './FactoryLines';
import {
    Actions,
    AddMoreButton,
    CancelButton,
    CancelCloseButton,
    CancelModal,
    ControlContainer,
    CustomModal,
    FactoryHeadContainer,
    FactoryLinesContainer,
    FactoryWrapper,
    Field,
} from './styled';

const INITIAL_FACTORY_LINE = (): CreateFactoryLine => ({
    customId: uuidv4(),
    name: '',
});

const INITIAL_FACTORY = (): CreateFactory => ({
    customId: uuidv4(),
    name: '',
    countryCode: '',
    address: '',
    factoryLines: [INITIAL_FACTORY_LINE()],
});

export const FactoryCreate = () => {
    const {t, i18n} = useTranslation();
    const [cancelModal, setCancelModal] = useState(false);

    const {showCreate, createPending} = useStore(factoryOperationsState$);

    const addressOptions = async (partialAddress: string) => {
        try {
            if (!partialAddress) return [{value: '', label: ''}];
            const lang =
                (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) || 'ru';
            const payload: Address = {
                address: partialAddress,
                lang,
            };

            const values = (await getAddressesSuggestList(payload)).data;
            return values.map((value) => ({value, label: value}));
        } catch (error) {
            console.error(error);
            return [{value: '', label: ''}];
        }
    };

    const formData = useForm<{factories: CreateFactory[]}>({
        mode: 'all',
        resolver: yupResolver(factoriesSchema),
        defaultValues: {
            factories: [INITIAL_FACTORY()],
        },
    });

    const {fields: formFactories, append, remove} = useFieldArray<CreateFactory>({
        control: formData.control,
        name: 'factories',
    });

    useEffect(() => {
        console.log('factories:', formFactories);
    }, [formFactories]);

    const addMoreFactory = () => {
        append(INITIAL_FACTORY());
    };

    const removeFactory = async (index?: number) => {
        if (typeof index !== 'number') return;
        if (formFactories.length > 1) {
            flushSync(() => remove(index));
            await formData.trigger();
        }
    };

    const onSubmit = async (data: {factories: CreateFactory[]}) => {
        const addressPayload = (partialAddress: string): Address => {
            const lang =
                (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) || 'ru';
            return {
                address: partialAddress,
                lang,
            };
        };

        const addressIdRequests = data.factories.map(async (f) => {
            if (!f.address) return f;
            try {
                const payload = addressPayload(f.address);
                const addressId = await getAddressInfoFromString(payload)
                    .then(formatAddressInfoToRequest)
                    .then(createAddress);

                return Object.assign(f, {addressId});
            } catch (error) {
                console.error(error);
                return f;
            }
        });

        const factoriesWithAddressId = await Promise.all(addressIdRequests);
        addFactories(factoriesWithAddressId);
    };

    const close = () => {
        setCancelModal(true);
    };

    const closeConfirmed = () => {
        setCancelModal(false);
        formData.reset();
        isCreateModalOpenChanged(false);
    };

    const {data: factoryCountries} = useStore(profileCountriesState$);
    const countries = React.useMemo(
        () =>
            factoryCountries.map(({code, country}: ProfileCountries) => ({
                label: t(`dictionaries.countries.${code}`),
                value: code,
                country,
            })) || [],
        [i18n.language, factoryCountries],
    );

    return (
        <CustomModal
            modalWidth={1}
            title={t('pages.profile.createFactory.title')}
            show={showCreate}
            onClose={close}
        >
            <CancelModal show={cancelModal} hideCloseButton onClose={() => {}}>
                <p>{t('pages.profile.createFactory.cancelText')}</p>
                <Actions>
                    <CancelCloseButton onClick={closeConfirmed}>
                        {t('pages.profile.createFactory.cancelClose')}
                    </CancelCloseButton>
                    <CancelButton
                        onClick={() => setCancelModal(false)}
                        variant={ButtonVariant.Outline}
                    >
                        {t('pages.profile.createFactory.cancel')}
                    </CancelButton>
                </Actions>
            </CancelModal>
            <form onSubmit={formData.handleSubmit(onSubmit)}>
                {formFactories?.map((factory, factoryIndex) => (
                    <FactoryWrapper key={factory.customId}>
                        <FactoryHeadContainer>
                            {t('pages.profile.createFactory.subtitle')}
                            <ControlContainer
                                disabled={formFactories.filter(Boolean).length <= 1}
                                onClick={() => removeFactory(factoryIndex)}
                            >
                                <Icon source={CrossIcon} width={16} height={16} />
                            </ControlContainer>
                        </FactoryHeadContainer>
                        <Field>
                            <Controller
                                name={`factories.${factoryIndex}.name`}
                                defaultValue={factory.name}
                                control={formData.control}
                                render={(props) => (
                                    <Input
                                        {...props}
                                        placeholder={t('pages.profile.createFactory.name')}
                                    />
                                )}
                            />
                        </Field>
                        <div style={{display: 'none'}}>
                            <Controller
                                name={`factories.${factoryIndex}.countryCode`}
                                defaultValue={COUNTRY_NAMING.short}
                                control={formData.control}
                                render={({value}) => <input type="hidden" value={value} readOnly />}
                            />
                        </div>
                        <Field>
                            <Controller
                                name={`factories.${factoryIndex}.address`}
                                control={formData.control}
                                defaultValue={factory.address}
                                render={({onChange, value, name}) => (
                                    <AsyncSelect
                                        name={name}
                                        placeholder={t('pages.profile.createFactory.address')}
                                        loadOptions={addressOptions}
                                        isClearable
                                        onChange={(v?: {value: string; label: string}) => {
                                            onChange(v?.value || '');
                                        }}
                                        value={value ? {value, label: value} : null}
                                    />
                                )}
                            />
                        </Field>
                        <FactoryLinesContainer>
                            <FactoryLines factoryIndex={factoryIndex} formData={formData} />
                        </FactoryLinesContainer>
                    </FactoryWrapper>
                ))}
                <AddMoreButton onClick={addMoreFactory} variant={ButtonVariant.Text} type="button">
                    + {t('pages.profile.createFactory.addFactory')}
                </AddMoreButton>
                <Actions>
                    <Button isLoading={createPending} disabled={!formData.formState.isValid}>
                        {t('pages.profile.createFactory.save')}
                    </Button>
                    <Button variant={ButtonVariant.Outline} type="button" onClick={close}>
                        {t('pages.profile.createFactory.cancel')}
                    </Button>
                </Actions>
            </form>
        </CustomModal>
    );
};
